<template>
  <BaseModal ref="modal">
    <UiModalDialog
      class="jps-curation-modal"
      :class="{ 'has-image': hasImage }"
      tabindex="-1"
      :no-header="false"
      :no-footer="true"
      :is-full-size="true"
      :appearance="{
        header: {
          isTransparent: true,
        }
      }"
    >
      <div class="jps-modal-content curation-modal-body">
        <div class="jps-curation-modal-image" v-if="hasImage">
          <ClientOnly>
            <UiIiifViewer v-if="hasImage && loaded" :image="viewerInfo">
              <template #info>
                <UiRightsTag
                  v-if="_info.rightsType"
                  :rights="_info.rightsType"
                  :rightsdescription="_info.rights"
                  class="curation-modal-rights-tag"
                />
                <a
                  class="jps-curation-modal-rights-info is-size-7"
                  v-if="_info.source"
                  :href="_info.sourceUrl"
                  >{{ ls(_info.source) }}</a
                >
              </template>
            </UiIiifViewer>
          </ClientOnly>
        </div>
        <div class="jps-curation-modal-content">
          <div
            class="jps-curation-modal-rights is-size-7"
            v-if="_info.rightsType || _info.rights"
          ></div>

          <!-- タイトル -->
          <div class="jps-curation-modal-title level">
            <h1 class="is-size-3-desktop is-size-4-touch has-text-left">
              {{ ls(_info.title) }}
            </h1>
            <!-- 外部リンク -->
            <div class="jps-curation-modal-link" v-if="_info.link">
              <UiButtonLinkRound
                :url="_info.link"
                styled="primary"
                size="large"
              >
                {{ isHina ? ls(dapLangGlobal.linkToSource) : lss('出典サイトで表示', 'Go to original site')}}
              </UiButtonLinkRound>
            </div>
          </div>
          <div class="jps-curation-modal-description">
            <p
              v-if="_info.description"
              class="jps-curation-modal-description-text is-size-6-desktop is-size-7-touch"
              v-html="ls(_info.description)"
            ></p>
          </div>
          <!-- 共通項目 -->
          <UiItemMetacommon
            v-if="label"
            :label="label"
            :item="item"
            :link="true"
            class="jps-item-meta-list"
            tag="div"
          />
          <!-- インタラクション -->
          <UiInteractPanel
            v-if="_info.data && !isWebParts"
            :type="_info.type"
            :data="_info.data"
            :image="favImage"
          />
          <div
            v-if="_info.internalLink && !isWebParts"
            class="jps-item-internallink generated-72"
          >
            <BaseLink
              class="jps-round-button jps-blue jps-large"
              :to="_info.internalLink"
            >{{ lss('続きを開く', 'See details') }}</BaseLink
            >
          </div>
        </div>
      </div>
    </UiModalDialog>
  </BaseModal>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'
import type { CurModalInfo } from '~/composables/curation/useCurationModal'
import type { BaseModal } from '#components'
import type { ModalProps } from '~/components/base/BaseModal.vue'

const { isHina } = useServiceType()
const { ls, lss } = useI18n()
const { compImageSelection, toViewerInfo } = useImageSelection()
const { isWebParts } = useWebParts()
const dataStore = useDataStore()

interface Props {
  info?: CurModalInfo
}
const props = withDefaults(defineProps<Props>(),{
})

const modal = ref<InstanceType<typeof BaseModal>>()
const _info: Ref<CurModalInfo> = ref()
const openAwait = async (options?: Props, modalProps?: ModalProps) => {
  loaded.value = false
  setOptionsValue(_info, 'info', props, options)
  loaded.value = true
  return await modal.value.openAwait(modalProps)
}
defineExpose({ openAwait })

const loaded:Ref<Boolean> = ref(false)
const hasImage = computed(() => {
  return viewerInfo.value != null
})

const viewerInfo = computed(() => {
  return toViewerInfo(compImageSelection(_info.value.image))
})

const item = computed(() => {
  if (_info.value.type === 'item' && _info.value.data) return _info.value.data
  if (_info.value.internalLink) {
    const itemId = _info.value.id || _info.value.internalLink?.params?.itemId
    return dataStore.item(itemId, true)
  }
})

//お気に入り用のイメージ設定
const iiifUrl: Ref<string> = ref(null)
const favImage = computed((): ImageSelection => {
  if (item.value && item.value.common.iiifUrl && iiifUrl.value) {
    return {
      imgType: 'IIIF',
      manifestUrl: item.value.common.iiifUrl,
      infoJsonUrl: iiifUrl.value,
    }
  } else if (props.info && _info.value.image) {
    return compImageSelection(_info.value.image)
  }
  return null
})

const label = computed(() => {
  if (item.value) {
    return dataStore.label(item.value.common.database)
  }
  return null
})
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps-curation-modal {
  position: fixed;
  bottom: 0;
  left: 0;
  overflow-y: auto;
  background-color: $white;
  border-radius: 0;

  .jps-curation-modal-image {
    .jps-rights-tag {
      margin-top: 0;
    }
  }

  .jps-curation-modal-rights {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    > * {
      margin-right: 0.5rem;
    }
    .jps-curation-modal-label {
      margin-right: 0.5rem;
      line-height: 100%;
      @include is-mobile {
        display: inline-block;
      }
    }
    @include is-mobile {
      flex-direction: column;
      align-items: flex-start;
    }
    .jps-curation-modal-rights-type.is-dark {
      border: none;
    }
    .jps-curation-modal-rights-info {
      background: none;
    }
  }

  .curation-modal-rights-tag {
    flex-shrink: 0;
  }
  .jps-curation-modal-rights-info {
    overflow-wrap: anywhere;
  }

  .jps-curation-modal-content {
    @include global-width-strict();
    @include is-mobile {
      margin: 1rem 1rem 3rem;
    }
    @include is-tablet {
      margin: 1rem auto 3rem;
    }
    .jps-curation-modal-title {
      align-items: center;
      justify-content: between-space;
      @include is-mobile {
        margin: 1rem 0;
      }
      @include is-tablet {
        margin: 2rem 0;
      }
      h1 {
        line-height: 125%;
      }
    }
    .jps-curation-modal-description {
      margin: 2rem 0;
      @include is-mobile {
        margin: 1rem 0;
      }
    }
    .jps-curation-modal-description-text {
      @include typo-paragraph();
    }
    .jps-curation-modal-link {
      line-height: 125%;
      @include is-mobile {
        margin: 1rem 0;
      }
      a {
        word-break: break-all;
        display: inline-flex;
        align-items: center;
        justify-content: flex-start;
      }
      i {
        margin-right: 0.25rem;
        font-size: 1rem;
      }
    }
    .jps-interact-panel {
      margin: 2rem;
      padding: 0;
      justify-content: center;
      @include is-mobile {
        margin: 1rem 0;
      }
    }
    .jps-item-meta-list {
      margin: 2rem 0;
      @include is-mobile {
        margin: 1rem 0;
      }
    }
    .jps-item-internallink {
      margin: 2rem auto;
      @include is-mobile {
        margin: 1rem 0;
      }
    }
  }
}

.generated-72 {
    text-align: center;
}
</style>
